import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Users, Search, Plus, X, ChevronRight, Phone, Mail, Building2,
  Tag, Trash2, BarChart3, TrendingUp, Send, Upload, Clock,
  ArrowLeft, RefreshCw, Loader2, CheckCircle, Calendar, Zap,
  List, Play
} from 'lucide-react';
import RichEmailEditor from './RichEmailEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800' },
  { value: 'contacted', label: 'Contacted', color: 'bg-amber-100 text-amber-800' },
  { value: 'demo', label: 'Demo', color: 'bg-purple-100 text-purple-800' },
  { value: 'trial', label: 'Trial', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'subscribed', label: 'Subscribed', color: 'bg-green-100 text-green-800' },
  { value: 'churned', label: 'Churned', color: 'bg-red-100 text-red-800' },
];

const SOURCES = ['website', 'signup', 'referral', 'social', 'email', 'manual', 'other'];

export default function CRMPanel() {
  const [view, setView] = useState('dashboard'); // dashboard | leads | lead-detail | drilldown
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAddLead, setShowAddLead] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownTitle, setDrilldownTitle] = useState('');
  const [addForm, setAddForm] = useState({ name: '', email: '', phone: '', organization: '', role: '', source: 'manual', stage: 'new', notes: '' });

  // New feature state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showScheduleEmail, setShowScheduleEmail] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showSequenceModal, setShowSequenceModal] = useState(false);
  const [showAssignSeq, setShowAssignSeq] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [bulkEmailForm, setBulkEmailForm] = useState({ stage: '', subject: '', body: '' });
  const [scheduleForm, setScheduleForm] = useState({ subject: '', body: '', send_at: '' });
  const [taskForm, setTaskForm] = useState({ title: '', description: '', due_date: '', type: 'follow-up' });
  const [seqForm, setSeqForm] = useState({ name: '', description: '', steps: [{ delay_days: 1, subject: '', body: '' }] });
  const [sequences, setSequences] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [uploadingCSV, setUploadingCSV] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', subject: '', body: '', category: 'custom' });

  useEffect(() => {
    if (view === 'dashboard') loadAnalytics();
    else if (view === 'leads') loadLeads();
  }, [view, filterStage, filterSource]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsRes, emailStatsRes] = await Promise.all([
        axios.get(`${API}/crm/analytics`),
        axios.get(`${API}/crm/email-stats`).catch(() => ({ data: {} })),
      ]);
      setAnalytics({ ...analyticsRes.data, emailStats: emailStatsRes.data });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStage) params.set('stage', filterStage);
      if (filterSource) params.set('source', filterSource);
      if (searchTerm) params.set('search', searchTerm);
      const res = await axios.get(`${API}/crm/leads?${params}`);
      setLeads(res.data.leads);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAddLead = async () => {
    if (!addForm.name || !addForm.email) return;
    try {
      await axios.post(`${API}/crm/leads`, addForm);
      setShowAddLead(false);
      setAddForm({ name: '', email: '', phone: '', organization: '', role: '', source: 'manual', stage: 'new', notes: '' });
      loadLeads();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to add lead'); }
  };

  const handleUpdateStage = async (leadId, newStage) => {
    try {
      await axios.put(`${API}/crm/leads/${leadId}`, { stage: newStage });
      if (selectedLead?.id === leadId) {
        const res = await axios.get(`${API}/crm/leads/${leadId}`);
        setSelectedLead(res.data);
      }
      loadLeads();
    } catch (e) { console.error(e); }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await axios.delete(`${API}/crm/leads/${leadId}`);
      setSelectedLead(null);
      setView('leads');
      loadLeads();
    } catch (e) { console.error(e); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedLead) return;
    try {
      await axios.post(`${API}/crm/leads/${selectedLead.id}/note`, { note: newNote });
      setNewNote('');
      const res = await axios.get(`${API}/crm/leads/${selectedLead.id}`);
      setSelectedLead(res.data);
    } catch (e) { console.error(e); }
  };

  const handleDrilldown = async (stage, title) => {
    try {
      const res = await axios.get(`${API}/crm/analytics/users-by-stage?stage=${stage}`);
      setDrilldownData(res.data.users);
      setDrilldownTitle(title);
      setView('drilldown');
    } catch (e) { console.error(e); }
  };

  const openLeadDetail = async (lead) => {
    try {
      const res = await axios.get(`${API}/crm/leads/${lead.id}`);
      setSelectedLead(res.data);
      setView('lead-detail');
      loadTasks(lead.id);
      loadEmailHistory(lead.id);
      loadSequences();
    } catch (e) { console.error(e); }
  };

  const stageColor = (stage) => STAGES.find(s => s.value === stage)?.color || 'bg-gray-100 text-gray-800';

  // --- New Feature Handlers ---
  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCSV(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/crm/leads/bulk-upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert(`Upload complete: ${res.data.created} created, ${res.data.updated} updated, ${res.data.skipped} skipped`);
      loadLeads();
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingCSV(false); e.target.value = ''; }
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject || !emailForm.body || !selectedLead) return;
    setSending(true);
    try {
      await axios.post(`${API}/crm/leads/${selectedLead.id}/send-email`, emailForm);
      alert('Email sent successfully!');
      setShowEmailModal(false);
      setEmailForm({ subject: '', body: '' });
      const updated = await axios.get(`${API}/crm/leads/${selectedLead.id}`);
      setSelectedLead(updated.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Email failed to send. Check your Resend quota at resend.com/overview';
      alert(detail);
    } finally { setSending(false); }
  };

  const handleBulkEmail = async () => {
    if (!bulkEmailForm.subject || !bulkEmailForm.body) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/crm/leads/bulk-email`, bulkEmailForm);
      alert(`Bulk email: ${res.data.sent} sent, ${res.data.failed} failed`);
      setShowBulkEmail(false);
      setBulkEmailForm({ stage: '', subject: '', body: '' });
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  const handleScheduleEmail = async () => {
    if (!scheduleForm.subject || !scheduleForm.send_at || !selectedLead) return;
    try {
      await axios.post(`${API}/crm/scheduled-emails`, { ...scheduleForm, to: selectedLead.email, lead_id: selectedLead.id });
      alert('Email scheduled!');
      setShowScheduleEmail(false);
      setScheduleForm({ subject: '', body: '', send_at: '' });
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleCreateTask = async () => {
    if (!taskForm.title || !selectedLead) return;
    try {
      await axios.post(`${API}/crm/tasks`, { ...taskForm, lead_id: selectedLead.id });
      alert('Task created!');
      setShowTaskModal(false);
      setTaskForm({ title: '', description: '', due_date: '', type: 'follow-up' });
      const updated = await axios.get(`${API}/crm/leads/${selectedLead.id}`);
      setSelectedLead(updated.data);
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleCreateSequence = async () => {
    if (!seqForm.name || seqForm.steps.length === 0) return;
    try {
      await axios.post(`${API}/crm/sequences`, seqForm);
      alert('Sequence created!');
      setShowSequenceModal(false);
      setSeqForm({ name: '', description: '', steps: [{ delay_days: 1, subject: '', body: '' }] });
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleAssignSequence = async (seqId) => {
    if (!selectedLead) return;
    try {
      const res = await axios.post(`${API}/crm/leads/${selectedLead.id}/assign-sequence`, { sequence_id: seqId });
      alert(res.data.message);
      setShowAssignSeq(false);
      const updated = await axios.get(`${API}/crm/leads/${selectedLead.id}`);
      setSelectedLead(updated.data);
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const loadSequences = async () => {
    try {
      const res = await axios.get(`${API}/crm/sequences`);
      setSequences(res.data.sequences || []);
    } catch (e) {}
  };

  const loadTasks = async (leadId) => {
    try {
      const res = await axios.get(`${API}/crm/tasks?lead_id=${leadId}`);
      setTasks(res.data.tasks || []);
    } catch (e) {}
  };

  const loadEmailHistory = async (leadId) => {
    try {
      const res = await axios.get(`${API}/crm/emails?lead_id=${leadId}`);
      setEmailHistory(res.data.emails || []);
    } catch (e) {}
  };

  const loadEmailTemplates = async () => {
    try {
      const res = await axios.get(`${API}/crm/email-templates`);
      setEmailTemplates(res.data.templates || []);
    } catch (e) {}
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject) return;
    try {
      if (editingTemplate) {
        await axios.put(`${API}/crm/email-templates/${editingTemplate}`, templateForm);
      } else {
        await axios.post(`${API}/crm/email-templates`, templateForm);
      }
      setShowTemplateEditor(false);
      setEditingTemplate(null);
      setTemplateForm({ name: '', subject: '', body: '', category: 'custom' });
      loadEmailTemplates();
    } catch (e) { alert('Failed to save template'); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/crm/email-templates/${id}`);
      loadEmailTemplates();
    } catch (e) {}
  };

  const applyTemplate = (template, target) => {
    if (target === 'individual') {
      setEmailForm({ subject: template.subject, body: template.body });
    } else if (target === 'bulk') {
      setBulkEmailForm(p => ({ ...p, subject: template.subject, body: template.body }));
    }
  };

  // Shared modals renderer — called in every view
  const renderModals = () => (
    <>
      {/* Send Email Modal */}
      {showEmailModal && selectedLead && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowEmailModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-3xl p-6 shadow-xl max-h-[85vh] overflow-y-auto" data-testid="send-email-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#0F1A2A] text-lg">Email {selectedLead.name}</h3>
              <button onClick={() => setShowEmailModal(false)} className="p-1 hover:bg-[#F4F2EC] rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#475569] mb-3">To: {selectedLead.email}</p>
            {emailTemplates.length > 0 && (
              <div className="mb-3">
                <label className="text-xs text-[#94A3B8] mb-1 block">Use Template:</label>
                <div className="flex flex-wrap gap-1.5">
                  {emailTemplates.map(t => (
                    <button key={t.id} onClick={() => applyTemplate(t, 'individual')} className="px-2.5 py-1 text-xs bg-[#F4F2EC] hover:bg-[#E2DFD8] rounded-full text-[#475569] font-medium">{t.name}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-3">
                <input value={emailForm.subject} onChange={e => setEmailForm(p => ({...p, subject: e.target.value}))} placeholder="Subject *" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="email-subject" />
                <RichEmailEditor value={emailForm.body} onChange={val => setEmailForm(p => ({...p, body: val}))} placeholder="Compose your email..." />
                <button onClick={handleSendEmail} disabled={sending || !emailForm.subject || !emailForm.body || emailForm.body === '<p><br></p>'} className="w-full py-2.5 bg-[#1B3A6B] text-white rounded-lg font-medium text-sm disabled:opacity-50" data-testid="email-send-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : <Send className="w-4 h-4 inline mr-2" />}Send Email
                </button>
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-2 block">Preview</label>
                <div className="border border-[#E2DFD8] rounded-lg overflow-hidden bg-[#F4F2EC] p-3" data-testid="email-preview">
                  <div style={{ fontFamily: 'Georgia, serif', maxWidth: 400, margin: '0 auto' }}>
                    <div style={{ background: '#1B3A6B', padding: '14px 18px', borderRadius: '8px 8px 0 0' }}>
                      <div style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>dsashun</div>
                    </div>
                    <div style={{ padding: '18px', background: '#fff', border: '1px solid #E2DFD8', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
                      {emailForm.subject && <div style={{ color: '#1B3A6B', fontWeight: 'bold', fontSize: 13, marginBottom: 10 }}>{emailForm.subject.replace(/\{\{name\}\}/g, selectedLead?.name || 'Friend')}</div>}
                      <div style={{ color: '#0F1A2A', fontSize: 12, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: (emailForm.body || '<p style="color:#94A3B8">Your message will appear here...</p>').replace(/\{\{name\}\}/g, selectedLead?.name || 'Friend') }} />
                    </div>
                    <div style={{ textAlign: 'center', color: '#94A3B8', fontSize: 9, marginTop: 8 }}>Sent from dsashun CRM</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Email Modal */}
      {showScheduleEmail && selectedLead && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowScheduleEmail(false)}>
          <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl" data-testid="schedule-email-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#0F1A2A] text-lg">Schedule Email</h3>
              <button onClick={() => setShowScheduleEmail(false)} className="p-1 hover:bg-[#F4F2EC] rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#475569] mb-3">To: {selectedLead.email}</p>
            <div className="space-y-3">
              <input type="datetime-local" value={scheduleForm.send_at} onChange={e => setScheduleForm(p => ({...p, send_at: e.target.value ? new Date(e.target.value).toISOString() : ''}))} className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="schedule-datetime" />
              <input value={scheduleForm.subject} onChange={e => setScheduleForm(p => ({...p, subject: e.target.value}))} placeholder="Subject *" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="schedule-subject" />
              <RichEmailEditor value={scheduleForm.body} onChange={val => setScheduleForm(p => ({...p, body: val}))} placeholder="Compose your scheduled email (use {{name}})..." />
              <button onClick={handleScheduleEmail} disabled={!scheduleForm.subject || !scheduleForm.send_at} className="w-full py-2.5 bg-[#1B3A6B] text-white rounded-lg font-medium text-sm disabled:opacity-50" data-testid="schedule-submit">
                <Clock className="w-4 h-4 inline mr-2" />Schedule Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {showTaskModal && selectedLead && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowTaskModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl" data-testid="create-task-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#0F1A2A] text-lg">New Task</h3>
              <button onClick={() => setShowTaskModal(false)} className="p-1 hover:bg-[#F4F2EC] rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <input value={taskForm.title} onChange={e => setTaskForm(p => ({...p, title: e.target.value}))} placeholder="Task title *" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="task-title" />
              <textarea value={taskForm.description} onChange={e => setTaskForm(p => ({...p, description: e.target.value}))} placeholder="Description" rows={2} className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="task-desc" />
              <div className="grid grid-cols-2 gap-3">
                <input type="date" value={taskForm.due_date} onChange={e => setTaskForm(p => ({...p, due_date: e.target.value}))} className="px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="task-date" />
                <select value={taskForm.type} onChange={e => setTaskForm(p => ({...p, type: e.target.value}))} className="px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="task-type">
                  <option value="follow-up">Follow-up</option><option value="call">Call</option><option value="email">Email</option><option value="meeting">Meeting</option>
                </select>
              </div>
              <button onClick={handleCreateTask} disabled={!taskForm.title} className="w-full py-2.5 bg-[#1B3A6B] text-white rounded-lg font-medium text-sm disabled:opacity-50" data-testid="task-submit">
                <Calendar className="w-4 h-4 inline mr-2" />Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Sequence Modal */}
      {showAssignSeq && selectedLead && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowAssignSeq(false)}>
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl" data-testid="assign-seq-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#0F1A2A] text-lg">Assign Sequence</h3>
              <button onClick={() => setShowAssignSeq(false)} className="p-1 hover:bg-[#F4F2EC] rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#475569] mb-3">Assign a follow-up sequence to {selectedLead.name}</p>
            {sequences.length === 0 ? (
              <p className="text-sm text-[#94A3B8] text-center py-4">No sequences created yet.</p>
            ) : (
              <div className="space-y-2">
                {sequences.map(s => (
                  <button key={s.id} onClick={() => handleAssignSequence(s.id)} className="w-full text-left p-3 border border-[#E2DFD8] rounded-lg hover:bg-[#F4F2EC]" data-testid={`assign-seq-${s.id}`}>
                    <p className="text-sm font-medium text-[#0F1A2A] flex items-center gap-2"><Zap className="w-4 h-4 text-[#B8860B]" />{s.name}</p>
                    <p className="text-xs text-[#94A3B8] mt-1">{s.steps?.length || 0} steps</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Template Editor Modal */}
      {showTemplateEditor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowTemplateEditor(false)}>
          <div className="bg-white rounded-xl w-full max-w-2xl p-6 shadow-xl max-h-[80vh] overflow-y-auto" data-testid="template-manager">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#0F1A2A] text-lg">Email Templates</h3>
              <button onClick={() => setShowTemplateEditor(false)} className="p-1 hover:bg-[#F4F2EC] rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-2 mb-4">
              {emailTemplates.map(t => (
                <div key={t.id} className="border border-[#E2DFD8] rounded-lg p-3 bg-[#F9F8F6]">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-[#0F1A2A]">{t.name}</span>
                      <span className="px-1.5 py-0.5 text-[10px] bg-[#E2DFD8] rounded text-[#475569] capitalize">{t.category}</span>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => { setEditingTemplate(t.id); setTemplateForm({ name: t.name, subject: t.subject, body: t.body, category: t.category }); }} className="text-xs px-2 py-1 text-[#1B3A6B] hover:bg-[#E2DFD8] rounded">Edit</button>
                      <button onClick={() => handleDeleteTemplate(t.id)} className="text-xs px-2 py-1 text-red-500 hover:bg-red-50 rounded">Delete</button>
                    </div>
                  </div>
                  <p className="text-xs text-[#475569] truncate">Subject: {t.subject}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-[#E2DFD8] pt-4">
              <h4 className="font-medium text-sm text-[#0F1A2A] mb-3">{editingTemplate ? 'Edit Template' : 'New Template'}</h4>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input value={templateForm.name} onChange={e => setTemplateForm(p => ({...p, name: e.target.value}))} placeholder="Template name *" className="px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="tpl-name" />
                  <select value={templateForm.category} onChange={e => setTemplateForm(p => ({...p, category: e.target.value}))} className="px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="tpl-category">
                    <option value="onboarding">Onboarding</option><option value="follow-up">Follow-up</option><option value="sales">Sales</option><option value="events">Events</option><option value="custom">Custom</option>
                  </select>
                </div>
                <input value={templateForm.subject} onChange={e => setTemplateForm(p => ({...p, subject: e.target.value}))} placeholder="Subject *" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="tpl-subject" />
                <textarea value={templateForm.body} onChange={e => setTemplateForm(p => ({...p, body: e.target.value}))} placeholder="Body" rows={5} className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="tpl-body" />
                <div className="flex gap-2">
                  <button onClick={handleSaveTemplate} disabled={!templateForm.name || !templateForm.subject} className="flex-1 py-2.5 bg-[#1B3A6B] text-white rounded-lg font-medium text-sm disabled:opacity-50" data-testid="tpl-save">{editingTemplate ? 'Update' : 'Create'} Template</button>
                  {editingTemplate && <button onClick={() => { setEditingTemplate(null); setTemplateForm({ name: '', subject: '', body: '', category: 'custom' }); }} className="px-4 py-2.5 bg-white border border-[#E2DFD8] rounded-lg text-sm">Cancel</button>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Email Modal */}
      {showBulkEmail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowBulkEmail(false)}>
          <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl" data-testid="bulk-email-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#0F1A2A] text-lg">Bulk Email</h3>
              <button onClick={() => setShowBulkEmail(false)} className="p-1 hover:bg-[#F4F2EC] rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <select value={bulkEmailForm.stage} onChange={e => setBulkEmailForm(p => ({...p, stage: e.target.value}))} className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="bulk-email-stage">
                <option value="">All Leads</option>
                {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              {emailTemplates.length > 0 && (
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1 block">Use Template:</label>
                  <div className="flex flex-wrap gap-1.5">
                    {emailTemplates.map(t => (
                      <button key={t.id} onClick={() => applyTemplate(t, 'bulk')} className="px-2.5 py-1 text-xs bg-[#F4F2EC] hover:bg-[#E2DFD8] rounded-full text-[#475569] font-medium">{t.name}</button>
                    ))}
                  </div>
                </div>
              )}
              <input value={bulkEmailForm.subject} onChange={e => setBulkEmailForm(p => ({...p, subject: e.target.value}))} placeholder="Subject *" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="bulk-email-subject" />
              <RichEmailEditor value={bulkEmailForm.body} onChange={val => setBulkEmailForm(p => ({...p, body: val}))} placeholder="Compose your email (use {{name}} for personalization)..." />
              <button onClick={handleBulkEmail} disabled={sending || !bulkEmailForm.subject || !bulkEmailForm.body || bulkEmailForm.body === '<p><br></p>'} className="w-full py-2.5 bg-[#1B3A6B] text-white rounded-lg font-medium text-sm disabled:opacity-50" data-testid="bulk-email-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : <Send className="w-4 h-4 inline mr-2" />}Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sequence Modal */}
      {showSequenceModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowSequenceModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-xl p-6 shadow-xl max-h-[80vh] overflow-y-auto" data-testid="sequence-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#0F1A2A] text-lg">Create Follow-up Sequence</h3>
              <button onClick={() => setShowSequenceModal(false)} className="p-1 hover:bg-[#F4F2EC] rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <input value={seqForm.name} onChange={e => setSeqForm(p => ({...p, name: e.target.value}))} placeholder="Sequence name *" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="seq-name" />
              <div className="border-t border-[#E2DFD8] pt-3">
                <p className="text-sm font-medium text-[#0F1A2A] mb-2">Email Steps</p>
                {seqForm.steps.map((step, i) => (
                  <div key={i} className="border border-[#E2DFD8] rounded-lg p-3 mb-2 bg-[#F9F8F6]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-[#B8860B]">Step {i + 1}</span>
                      {seqForm.steps.length > 1 && <button onClick={() => setSeqForm(p => ({...p, steps: p.steps.filter((_, j) => j !== i)}))} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>}
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div><label className="text-[10px] text-[#94A3B8]">Delay (days)</label><input type="number" min={1} value={step.delay_days} onChange={e => { const s = [...seqForm.steps]; s[i].delay_days = parseInt(e.target.value) || 1; setSeqForm(p => ({...p, steps: s})); }} className="w-full px-2 py-1.5 border border-[#E2DFD8] rounded text-sm" /></div>
                      <div className="col-span-2"><label className="text-[10px] text-[#94A3B8]">Subject</label><input value={step.subject} onChange={e => { const s = [...seqForm.steps]; s[i].subject = e.target.value; setSeqForm(p => ({...p, steps: s})); }} className="w-full px-2 py-1.5 border border-[#E2DFD8] rounded text-sm" /></div>
                    </div>
                    <textarea value={step.body} onChange={e => { const s = [...seqForm.steps]; s[i].body = e.target.value; setSeqForm(p => ({...p, steps: s})); }} placeholder="Body (use {{name}})" rows={2} className="w-full px-2 py-1.5 border border-[#E2DFD8] rounded text-sm" />
                  </div>
                ))}
                <button onClick={() => setSeqForm(p => ({...p, steps: [...p.steps, { delay_days: p.steps.length + 1, subject: '', body: '' }]}))} className="text-sm text-[#1B3A6B] font-medium hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Add Step</button>
              </div>
              <button onClick={handleCreateSequence} disabled={!seqForm.name || seqForm.steps.some(s => !s.subject)} className="w-full py-2.5 bg-[#B8860B] text-white rounded-lg font-medium text-sm disabled:opacity-50" data-testid="seq-submit"><Zap className="w-4 h-4 inline mr-2" />Create Sequence</button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // --- DASHBOARD VIEW ---
  if (view === 'dashboard') {
    return (
      <div>
      {renderModals()}
      <div data-testid="crm-dashboard">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">CRM & Leads</h2>
          <div className="flex gap-2">
            <button onClick={() => { loadEmailTemplates(); setShowTemplateEditor(true); }} className="btn-secondary py-2 px-4 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="crm-manage-templates">
              <Mail className="w-4 h-4" /> Templates
            </button>
            <button onClick={() => setView('leads')} className="btn-primary py-2 px-4 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="crm-view-leads">
              <Users className="w-4 h-4" /> View Leads
            </button>
            <button onClick={loadAnalytics} className="btn-secondary p-2 rounded-lg"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>

        {loading ? <div className="text-center py-12 text-[#475569]">Loading...</div> : analytics && (
          <>
            {/* Top Stats Row — All Clickable */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <button onClick={() => handleDrilldown('', 'All Users')} className="bg-white p-5 rounded-xl shadow-sm text-left hover:shadow-md hover:ring-2 hover:ring-[#1B3A6B]/20 transition-all" data-testid="crm-widget-total">
                <div className="text-3xl font-bold text-[#1B3A6B]">{analytics.users.total}</div>
                <div className="text-sm text-[#475569] mt-1">Total Users</div>
              </button>
              <button onClick={() => handleDrilldown('active', 'Active Users')} className="bg-white p-5 rounded-xl shadow-sm text-left hover:shadow-md hover:ring-2 hover:ring-green-200 transition-all" data-testid="crm-widget-active">
                <div className="text-3xl font-bold text-green-600">{analytics.users.active}</div>
                <div className="text-sm text-[#475569] mt-1">Active Users</div>
              </button>
              <button onClick={() => handleDrilldown('paid', 'Paid Users')} className="bg-white p-5 rounded-xl shadow-sm text-left hover:shadow-md hover:ring-2 hover:ring-[#B8860B]/20 transition-all" data-testid="crm-widget-paid">
                <div className="text-3xl font-bold text-[#B8860B]">{analytics.users.paid}</div>
                <div className="text-sm text-[#475569] mt-1">Paid Users</div>
              </button>
              <button onClick={() => handleDrilldown('trial', 'Trial Users')} className="bg-white p-5 rounded-xl shadow-sm text-left hover:shadow-md hover:ring-2 hover:ring-cyan-200 transition-all" data-testid="crm-widget-trial">
                <div className="text-3xl font-bold text-cyan-600">{analytics.users.trial}</div>
                <div className="text-sm text-[#475569] mt-1">In Trial</div>
              </button>
              <button onClick={() => handleDrilldown('churned', 'Churned Users')} className="bg-white p-5 rounded-xl shadow-sm text-left hover:shadow-md hover:ring-2 hover:ring-red-200 transition-all" data-testid="crm-widget-churned">
                <div className="text-3xl font-bold text-red-500">{analytics.users.churned}</div>
                <div className="text-sm text-[#475569] mt-1">Churned</div>
              </button>
            </div>

            {/* Conversion Rate */}
            <div className="bg-white p-5 rounded-xl shadow-sm mb-6" data-testid="crm-conversion">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-[#0F1A2A]">Conversion Rate</h3>
                <span className="text-2xl font-bold text-[#1B3A6B]">{analytics.users.conversion_rate}%</span>
              </div>
              <div className="w-full bg-[#F4F2EC] rounded-full h-3">
                <div className="h-3 rounded-full bg-[#1B3A6B] transition-all" style={{ width: `${Math.min(100, analytics.users.conversion_rate)}%` }} />
              </div>
              <p className="text-xs text-[#94A3B8] mt-2">{analytics.users.paid} paid out of {analytics.users.total} total users</p>
            </div>

            {/* Email Delivery Stats */}
            {analytics.emailStats?.total > 0 && (
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="crm-email-stats">
                {[
                  { label: 'Total Sent', value: analytics.emailStats.total, color: 'text-[#1B3A6B]' },
                  { label: 'Delivered', value: analytics.emailStats.delivered, color: 'text-green-600' },
                  { label: 'Sent', value: analytics.emailStats.sent, color: 'text-blue-600' },
                  { label: 'Bounced', value: analytics.emailStats.bounced, color: 'text-red-500' },
                  { label: 'Failed', value: analytics.emailStats.failed, color: 'text-red-600' },
                ].map((s, i) => (
                  <div key={i} className="bg-white p-4 rounded-xl shadow-sm">
                    <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                    <div className="text-[10px] text-[#94A3B8] mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Lead Funnel */}
              <div className="bg-white p-6 rounded-xl shadow-sm" data-testid="crm-funnel">
                <h3 className="font-semibold text-[#0F1A2A] mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#B8860B]" /> Lead Funnel</h3>
                <div className="space-y-3">
                  {STAGES.map(s => {
                    const count = analytics.funnel[s.value] || 0;
                    const maxCount = Math.max(1, ...Object.values(analytics.funnel));
                    return (
                      <button key={s.value} onClick={() => { setFilterStage(s.value); setView('leads'); }} className="w-full flex items-center gap-3 group hover:bg-[#F9F8F6] rounded-lg p-1.5 -ml-1.5 transition-colors">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color} w-24 text-center`}>{s.label}</span>
                        <div className="flex-1 h-2.5 bg-[#F4F2EC] rounded-full overflow-hidden">
                          <div className="h-full bg-[#1B3A6B] rounded-full transition-all" style={{ width: `${(count / maxCount) * 100}%` }} />
                        </div>
                        <span className="font-medium text-[#0F1A2A] w-8 text-right">{count}</span>
                        <ChevronRight className="w-4 h-4 text-[#94A3B8] opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-[#94A3B8] mt-3">Total leads: {analytics.total_leads}</p>
              </div>

              {/* Lead Sources */}
              <div className="bg-white p-6 rounded-xl shadow-sm" data-testid="crm-sources">
                <h3 className="font-semibold text-[#0F1A2A] mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#B8860B]" /> Lead Sources</h3>
                {analytics.sources.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.sources.map(s => {
                      const maxCount = Math.max(1, ...analytics.sources.map(x => x.count));
                      return (
                        <button key={s.source} onClick={() => { setFilterSource(s.source); setView('leads'); }} className="w-full flex items-center gap-3 group hover:bg-[#F9F8F6] rounded-lg p-1.5 -ml-1.5 transition-colors">
                          <span className="text-sm text-[#475569] capitalize w-20">{s.source}</span>
                          <div className="flex-1 h-2.5 bg-[#F4F2EC] rounded-full overflow-hidden">
                            <div className="h-full bg-[#B8860B] rounded-full transition-all" style={{ width: `${(s.count / maxCount) * 100}%` }} />
                          </div>
                          <span className="font-medium text-[#0F1A2A] w-8 text-right">{s.count}</span>
                          <ChevronRight className="w-4 h-4 text-[#94A3B8] opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      );
                    })}
                  </div>
                ) : <p className="text-sm text-[#94A3B8] text-center py-4">No leads yet</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Monthly Signups */}
              <div className="bg-white p-6 rounded-xl shadow-sm" data-testid="crm-signups">
                <h3 className="font-semibold text-[#0F1A2A] mb-4">Monthly Signups</h3>
                <div className="space-y-2">
                  {analytics.monthly_signups.slice(0, 6).map(m => (
                    <div key={m.month} className="flex items-center justify-between">
                      <span className="text-sm text-[#475569]">{m.month}</span>
                      <span className="font-medium text-[#0F1A2A]">{m.count}</span>
                    </div>
                  ))}
                  {analytics.monthly_signups.length === 0 && <p className="text-sm text-[#94A3B8]">No data yet</p>}
                </div>
              </div>

              {/* Revenue Trend */}
              <div className="bg-white p-6 rounded-xl shadow-sm" data-testid="crm-revenue-trend">
                <h3 className="font-semibold text-[#0F1A2A] mb-4">Revenue Trend</h3>
                <div className="space-y-2">
                  {analytics.revenue_trend.slice(0, 6).map(r => (
                    <div key={r.month} className="flex items-center justify-between">
                      <span className="text-sm text-[#475569]">{r.month}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-[#94A3B8]">{r.count} txn</span>
                        <span className="font-medium text-green-600">${r.revenue.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                  {analytics.revenue_trend.length === 0 && <p className="text-sm text-[#94A3B8]">No revenue data yet</p>}
                </div>
              </div>
            </div>
          </>
        )}

      </div>
      </div>
    );
  }

  // --- DRILLDOWN VIEW ---
  if (view === 'drilldown') {
    return (
      <div>
      {renderModals()}
      <div data-testid="crm-drilldown">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => setView('dashboard')} className="p-2 hover:bg-[#F4F2EC] rounded-lg"><ArrowLeft className="w-5 h-5 text-[#475569]" /></button>
          <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">{drilldownTitle}</h2>
          <span className="text-sm text-[#94A3B8]">({drilldownData?.length || 0} users)</span>
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead className="bg-[#F4F2EC]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Tier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Generations</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#475569]">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2DFD8]">
              {drilldownData?.map(u => (
                <tr key={u.id} className="hover:bg-[#F9F8F6]">
                  <td className="px-6 py-3 text-sm font-medium text-[#0F1A2A]">{u.name}</td>
                  <td className="px-6 py-3 text-sm text-[#475569]">{u.email}</td>
                  <td className="px-6 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#1B3A6B] text-white capitalize">{u.tier}</span></td>
                  <td className="px-6 py-3 text-sm text-[#475569]">{u.generations}</td>
                  <td className="px-6 py-3 text-sm text-[#475569]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    );
  }

  // --- LEAD DETAIL VIEW ---
  if (view === 'lead-detail' && selectedLead) {
    return (
      <div>
        {renderModals()}
        <div data-testid="crm-lead-detail">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => { setView('leads'); loadLeads(); }} className="p-2 hover:bg-[#F4F2EC] rounded-lg"><ArrowLeft className="w-5 h-5 text-[#475569]" /></button>
          <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">{selectedLead.name || 'Unnamed Lead'}</h2>
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${stageColor(selectedLead.stage)}`}>{selectedLead.stage}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="font-semibold text-[#0F1A2A] mb-4">Contact Info</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-[#475569]"><Mail className="w-4 h-4" /> {selectedLead.email || '-'}</div>
                <div className="flex items-center gap-2 text-[#475569]"><Phone className="w-4 h-4" /> {selectedLead.phone || '-'}</div>
                <div className="flex items-center gap-2 text-[#475569]"><Building2 className="w-4 h-4" /> {selectedLead.organization || '-'}</div>
                <div className="flex items-center gap-2 text-[#475569]"><Tag className="w-4 h-4" /> {selectedLead.source || '-'}</div>
              </div>
            </div>

            {/* Stage Changer */}
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="font-semibold text-[#0F1A2A] mb-3">Pipeline Stage</h3>
              <div className="space-y-1.5">
                {STAGES.map(s => (
                  <button key={s.value} onClick={() => handleUpdateStage(selectedLead.id, s.value)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${selectedLead.stage === s.value ? s.color + ' ring-2 ring-[#1B3A6B]/20' : 'text-[#475569] hover:bg-[#F4F2EC]'}`}
                    data-testid={`stage-btn-${s.value}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={() => handleDeleteLead(selectedLead.id)} className="w-full flex items-center justify-center gap-2 py-2.5 text-red-500 hover:bg-red-50 rounded-lg text-sm font-medium" data-testid="delete-lead-btn">
              <Trash2 className="w-4 h-4" /> Delete Lead
            </button>

            {/* Actions */}
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="font-semibold text-[#0F1A2A] mb-3">Actions</h3>
              <div className="space-y-2">
                <button onClick={() => { setShowEmailModal(true); loadEmailTemplates(); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg" data-testid="send-email-btn">
                  <Send className="w-4 h-4" /> Send Email
                </button>
                <button onClick={() => setShowScheduleEmail(true)} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg" data-testid="schedule-email-btn">
                  <Clock className="w-4 h-4" /> Schedule Email
                </button>
                <button onClick={() => setShowTaskModal(true)} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-[#1B3A6B] hover:bg-[#F4F2EC] rounded-lg" data-testid="create-task-btn">
                  <Calendar className="w-4 h-4" /> Add Task
                </button>
                <button onClick={() => { loadSequences(); setShowAssignSeq(true); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-[#B8860B] hover:bg-[#FFF8E7] rounded-lg" data-testid="assign-sequence-btn">
                  <Zap className="w-4 h-4" /> Assign Sequence
                </button>
              </div>
            </div>
          </div>

          {/* Activity, Tasks, Email History */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tasks */}
            {tasks.length > 0 && (
              <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="lead-tasks">
                <h3 className="font-semibold text-[#0F1A2A] mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-[#B8860B]" /> Tasks</h3>
                <div className="space-y-2">
                  {tasks.map(t => (
                    <div key={t.id} className={`flex items-center justify-between p-3 rounded-lg ${t.status === 'completed' ? 'bg-green-50' : 'bg-[#F9F8F6]'}`}>
                      <div>
                        <p className={`text-sm font-medium ${t.status === 'completed' ? 'line-through text-[#94A3B8]' : 'text-[#0F1A2A]'}`}>{t.title}</p>
                        <p className="text-xs text-[#94A3B8]">{t.type} {t.due_date ? `· Due: ${new Date(t.due_date).toLocaleDateString()}` : ''}</p>
                      </div>
                      {t.status !== 'completed' && (
                        <button onClick={async () => { await axios.put(`${API}/crm/tasks/${t.id}`, { status: 'completed' }); loadTasks(selectedLead.id); const u = await axios.get(`${API}/crm/leads/${selectedLead.id}`); setSelectedLead(u.data); }} className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full hover:bg-green-200">
                          <CheckCircle className="w-3 h-3 inline mr-1" />Done
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Email History */}
            {emailHistory.length > 0 && (
              <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="lead-email-history">
                <h3 className="font-semibold text-[#0F1A2A] mb-3 flex items-center gap-2"><Mail className="w-4 h-4 text-[#1B3A6B]" /> Email History</h3>
                <div className="space-y-2">
                  {emailHistory.map(e => {
                    const status = e.delivery_status || e.status;
                    const statusStyle = status === 'delivered' ? 'bg-green-100 text-green-800' : status === 'bounced' ? 'bg-red-100 text-red-800' : status === 'complained' ? 'bg-red-100 text-red-800' : status === 'sent' ? 'bg-blue-100 text-blue-800' : status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800';
                    return (
                      <div key={e.id} className="flex items-center justify-between p-3 bg-[#F9F8F6] rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-[#0F1A2A]">{e.subject}</p>
                          <p className="text-xs text-[#94A3B8]">{new Date(e.created_at).toLocaleString()}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle}`}>{status}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Activity & Notes */}
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h3 className="font-semibold text-[#0F1A2A] mb-4">Activity & Notes</h3>

              {/* Add Note */}
              <div className="flex gap-2 mb-4">
                <input value={newNote} onChange={e => setNewNote(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddNote()} placeholder="Add a note..." className="flex-1 px-3 py-2.5 text-sm border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B]" data-testid="lead-note-input" />
                <button onClick={handleAddNote} disabled={!newNote.trim()} className="px-4 py-2.5 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-[#254B8A]" data-testid="lead-add-note-btn">Add</button>
              </div>

              {/* Activity Timeline */}
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {(selectedLead.activity || []).slice().reverse().map((a, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <div className="w-2 h-2 rounded-full bg-[#1B3A6B] mt-2 flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#0F1A2A]">{a.action}</p>
                      <p className="text-xs text-[#94A3B8] mt-0.5">{a.by} &middot; {a.at ? new Date(a.at).toLocaleString() : ''}</p>
                    </div>
                  </div>
                ))}
                {(!selectedLead.activity || selectedLead.activity.length === 0) && (
                  <p className="text-sm text-[#94A3B8] text-center py-4">No activity yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    );
  }

  // --- LEADS LIST VIEW ---
  return (
    <div>
      {renderModals()}
      <div data-testid="crm-leads-list">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => { setView('dashboard'); setFilterStage(''); setFilterSource(''); }} className="p-2 hover:bg-[#F4F2EC] rounded-lg"><ArrowLeft className="w-5 h-5 text-[#475569]" /></button>
          <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">Leads</h2>
          <span className="text-sm text-[#94A3B8]">({leads.length})</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a href={`${API}/crm/leads/template`} download className="btn-secondary py-2 px-3 rounded-lg font-medium flex items-center gap-2 text-sm text-[#475569] no-underline" data-testid="crm-download-template">
            <List className="w-4 h-4" /> Template
          </a>
          <label className="btn-secondary py-2 px-3 rounded-lg font-medium flex items-center gap-2 text-sm cursor-pointer" data-testid="crm-bulk-upload-btn">
            <Upload className="w-4 h-4" /> {uploadingCSV ? 'Uploading...' : 'CSV Upload'}
            <input type="file" accept=".csv" onChange={handleCSVUpload} className="hidden" />
          </label>
          <button onClick={() => { setShowBulkEmail(true); loadEmailTemplates(); }} className="btn-secondary py-2 px-3 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="crm-bulk-email-btn">
            <Send className="w-4 h-4" /> Bulk Email
          </button>
          <button onClick={() => setShowSequenceModal(true)} className="btn-secondary py-2 px-3 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="crm-create-sequence-btn">
            <Zap className="w-4 h-4" /> Sequences
          </button>
          <button onClick={() => setShowAddLead(true)} className="btn-primary py-2 px-3 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="crm-add-lead-btn">
            <Plus className="w-4 h-4" /> Add Lead
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadLeads()} placeholder="Search leads..." className="w-full pl-9 pr-4 py-2.5 bg-white rounded-lg border border-[#E2DFD8] text-sm focus:ring-2 focus:ring-[#1B3A6B]" data-testid="crm-search" />
        </div>
        <select value={filterStage} onChange={e => setFilterStage(e.target.value)} className="px-3 py-2.5 bg-white border border-[#E2DFD8] rounded-lg text-sm" data-testid="crm-filter-stage">
          <option value="">All Stages</option>
          {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={filterSource} onChange={e => setFilterSource(e.target.value)} className="px-3 py-2.5 bg-white border border-[#E2DFD8] rounded-lg text-sm" data-testid="crm-filter-source">
          <option value="">All Sources</option>
          {SOURCES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
        </select>
        <button onClick={loadLeads} className="p-2.5 bg-white border border-[#E2DFD8] rounded-lg hover:bg-[#F4F2EC]"><RefreshCw className="w-4 h-4 text-[#475569]" /></button>
      </div>

      {/* Leads Table */}
      {loading ? <div className="text-center py-12 text-[#475569]">Loading...</div> : leads.length === 0 ? (
        <div className="text-center py-12 text-[#475569]">No leads found. <button onClick={() => setShowAddLead(true)} className="text-[#1B3A6B] font-medium hover:underline">Add your first lead</button></div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-[#F4F2EC]">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-medium text-[#475569]">Name</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-[#475569]">Email</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-[#475569]">Organization</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-[#475569]">Stage</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-[#475569]">Source</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-[#475569]">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2DFD8]">
              {leads.map(l => (
                <tr key={l.id} onClick={() => openLeadDetail(l)} className="hover:bg-[#F9F8F6] cursor-pointer" data-testid={`lead-row-${l.id}`}>
                  <td className="px-5 py-3 text-sm font-medium text-[#0F1A2A]">{l.name || '-'}</td>
                  <td className="px-5 py-3 text-sm text-[#475569]">{l.email}</td>
                  <td className="px-5 py-3 text-sm text-[#475569]">{l.organization || '-'}</td>
                  <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${stageColor(l.stage)}`}>{l.stage}</span></td>
                  <td className="px-5 py-3 text-sm text-[#475569] capitalize">{l.source || '-'}</td>
                  <td className="px-5 py-3 text-sm text-[#94A3B8]">{l.updated_at ? new Date(l.updated_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Lead Modal */}
      {showAddLead && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowAddLead(false)}>
          <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl" data-testid="crm-add-lead-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#0F1A2A] text-lg">Add New Lead</h3>
              <button onClick={() => setShowAddLead(false)} className="p-1 hover:bg-[#F4F2EC] rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input value={addForm.name} onChange={e => setAddForm(p => ({...p, name: e.target.value}))} placeholder="Name *" className="px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="lead-form-name" />
                <input value={addForm.email} onChange={e => setAddForm(p => ({...p, email: e.target.value}))} placeholder="Email *" className="px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="lead-form-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input value={addForm.phone} onChange={e => setAddForm(p => ({...p, phone: e.target.value}))} placeholder="Phone" className="px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="lead-form-phone" />
                <input value={addForm.organization} onChange={e => setAddForm(p => ({...p, organization: e.target.value}))} placeholder="Organization / Church" className="px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="lead-form-org" />
              </div>
              <input value={addForm.role} onChange={e => setAddForm(p => ({...p, role: e.target.value}))} placeholder="Role (e.g. Pastor, Ministry Leader)" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="lead-form-role" />
              <div className="grid grid-cols-2 gap-3">
                <select value={addForm.stage} onChange={e => setAddForm(p => ({...p, stage: e.target.value}))} className="px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="lead-form-stage">
                  {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <select value={addForm.source} onChange={e => setAddForm(p => ({...p, source: e.target.value}))} className="px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="lead-form-source">
                  {SOURCES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
                </select>
              </div>
              <textarea value={addForm.notes} onChange={e => setAddForm(p => ({...p, notes: e.target.value}))} placeholder="Notes..." rows={2} className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="lead-form-notes" />
              <button onClick={handleAddLead} disabled={!addForm.name || !addForm.email} className="w-full py-2.5 bg-[#1B3A6B] text-white rounded-lg font-medium text-sm disabled:opacity-50 hover:bg-[#254B8A]" data-testid="lead-form-submit">
                Add Lead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

